import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Download, Save, Users, ScanLine, CheckCircle, Clock, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import BarcodeScannerDialog from '@/components/BarcodeScannerDialog';
import { autoDecodeCF } from '@/lib/cfDecoder';
import DatePickerField from '@/components/DatePickerField';

const EMPTY_GARANTE = { nome: '', cognome: '', data_nascita: '', luogo_nascita: '', indirizzo: '', codice_fiscale: '', parentela: '', telefono: '' };

const PARENTELA_OPTIONS = [
  { value: 'figlio/a', label: 'Figlio/a' },
  { value: 'coniuge', label: 'Coniuge' },
  { value: 'nipote', label: 'Nipote' },
  { value: 'fratello/sorella', label: 'Fratello/Sorella' },
  { value: 'tutore legale', label: 'Tutore Legale' },
  { value: 'amministratore di sostegno', label: 'Amministratore di Sostegno' },
  { value: 'altro', label: 'Altro' },
];

function GaranteForm({ garante, index, total, onChange, onRemove, onScanCF }) {
  const update = (field, value) => onChange(index, { ...garante, [field]: value });
  return (
    <div className="p-4 rounded-lg border border-slate-200 bg-white space-y-3" data-testid={`garante-form-${index}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600">
          Garante {total > 1 ? index + 1 : ''} {garante.nome && garante.cognome ? `- ${garante.cognome} ${garante.nome}` : ''}
        </p>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => onScanCF(index)} className="h-7 text-[10px] text-brand hover:bg-blue-50" data-testid={`scan-cf-garante-${index}`}>
            <ScanLine className="w-3 h-3 mr-1" />Scansiona CF
          </Button>
          {total > 1 && (
            <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" data-testid={`remove-garante-${index}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Nome *</Label>
          <Input value={garante.nome} onChange={e => update('nome', e.target.value)} placeholder="Nome" data-testid={`garante-nome-${index}`} /></div>
        <div className="space-y-1"><Label className="text-xs">Cognome *</Label>
          <Input value={garante.cognome} onChange={e => update('cognome', e.target.value)} placeholder="Cognome" data-testid={`garante-cognome-${index}`} /></div>
        <div className="space-y-1"><Label className="text-xs">Data di Nascita</Label>
          <DatePickerField value={garante.data_nascita} onChange={v => update('data_nascita', v)} /></div>
        <div className="space-y-1"><Label className="text-xs">Luogo di Nascita</Label>
          <Input value={garante.luogo_nascita} onChange={e => update('luogo_nascita', e.target.value)} placeholder="es. Avezzano (AQ)" /></div>
        <div className="space-y-1"><Label className="text-xs">Codice Fiscale</Label>
          <Input value={garante.codice_fiscale} onChange={e => {
            const val = e.target.value.toUpperCase();
            update('codice_fiscale', val);
            autoDecodeCF(val, (r) => {
              onChange(index, { ...garante, codice_fiscale: val, data_nascita: r.data_nascita || garante.data_nascita, luogo_nascita: r.luogo_nascita || garante.luogo_nascita });
              toast.success(`CF garante decodificato: ${r.luogo_nascita || ''}${r.eta != null ? `, ${r.eta} anni` : ''}`);
            });
          }} className={garante.codice_fiscale ? 'border-emerald-300 bg-emerald-50/50' : ''} placeholder="CF" /></div>
        <div className="space-y-1"><Label className="text-xs">Indirizzo Residenza</Label>
          <Input value={garante.indirizzo} onChange={e => update('indirizzo', e.target.value)} placeholder="Via, Citta" /></div>
        <div className="space-y-1"><Label className="text-xs">Telefono</Label>
          <Input value={garante.telefono || ''} onChange={e => update('telefono', e.target.value)} placeholder="Cellulare" data-testid={`garante-telefono-${index}`} /></div>
        <div className="space-y-1"><Label className="text-xs">Parentela</Label>
          <Select value={garante.parentela} onValueChange={v => update('parentela', v)}>
            <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>{PARENTELA_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function ContrattoTab({ ospite }) {
  const { hasRole } = useAuth();
  const [contratti, setContratti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scannerIdx, setScannerIdx] = useState(null);
  const [generatingAllegato, setGeneratingAllegato] = useState(false);
  const [allegato, setAllegato] = useState({
    diaria_mensile: '', regime: 'privato', tipologia_camera: 'DOPPIA',
    servizio_lavanderia: 'si', cambio_medico_base: 'no', servizi_extra: 'no',
    servizi_extra_note: '', ritiro_presidi: 'si', altro: '',
    data_allegato: new Date().toISOString().slice(0, 10)
  });

  const [garanti, setGaranti] = useState([{ ...EMPTY_GARANTE }]);
  const [datiContratto, setDatiContratto] = useState({
    retta_mensile: 1600, data_stipula: new Date().toISOString().slice(0, 10),
    luogo_stipula: 'Celano', note_aggiuntive: ''
  });

  // Pre-fill first garante from referenti
  useEffect(() => {
    if (ospite?.referenti?.length > 0) {
      const newGaranti = ospite.referenti.map(ref => {
        const parts = (ref.nome || '').split(' ');
        return {
          ...EMPTY_GARANTE,
          nome: parts.length > 1 ? parts.slice(1).join(' ') : parts[0] || '',
          cognome: parts.length > 1 ? parts[0] : '',
          parentela: ref.parentela || ''
        };
      });
      if (newGaranti.length > 0) setGaranti(newGaranti);
    }
  }, [ospite]);

  const fetchContratti = useCallback(async () => {
    if (!ospite?.id) return;
    setLoading(true);
    try { const res = await api.get(`/contratti/${ospite.id}`); setContratti(res.data); }
    catch (e) { console.error('Errore caricamento contratti:', e); }
    setLoading(false);
  }, [ospite?.id]);
  useEffect(() => { fetchContratti(); }, [fetchContratti]);

  const updateGarante = (idx, data) => setGaranti(g => g.map((v, i) => i === idx ? data : v));
  const addGarante = () => setGaranti(g => [...g, { ...EMPTY_GARANTE }]);
  const removeGarante = (idx) => setGaranti(g => g.filter((_, i) => i !== idx));

  const buildPayload = () => ({
    ospite_id: ospite.id,
    garanti: garanti.map(g => ({
      nome: g.nome, cognome: g.cognome, data_nascita: g.data_nascita,
      luogo_nascita: g.luogo_nascita, indirizzo_residenza: g.indirizzo,
      codice_fiscale: g.codice_fiscale, parentela: g.parentela
    })),
    retta_mensile: parseFloat(datiContratto.retta_mensile) || 1600,
    data_stipula: datiContratto.data_stipula,
    luogo_stipula: datiContratto.luogo_stipula,
    note_aggiuntive: datiContratto.note_aggiuntive
  });

  const validate = () => garanti.every(g => g.nome && g.cognome);

  const handleGenerate = async () => {
    if (!validate()) { toast.error('Inserisci nome e cognome di ogni garante'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/contratto/genera', buildPayload(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Contratto_${ospite.cognome}_${ospite.nome}_${new Date().toISOString().slice(0, 10)}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contratto DOCX generato e scaricato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore generazione'); }
    setGenerating(false);
  };

  const handleSave = async () => {
    if (!validate()) { toast.error('Inserisci nome e cognome di ogni garante'); return; }
    setSaving(true);
    try { await api.post('/contratto/salva', buildPayload()); toast.success('Bozza salvata'); fetchContratti(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    setSaving(false);
  };

  const triState = (v) => (v === 'si' ? true : v === 'no' ? false : null);

  const handleGenerateAllegato = async () => {
    if (!validate()) { toast.error('Inserisci nome e cognome di ogni garante'); return; }
    setGeneratingAllegato(true);
    try {
      const payload = {
        ospite_id: ospite.id,
        garanti: garanti.map(g => ({
          nome: g.nome, cognome: g.cognome, data_nascita: g.data_nascita,
          luogo_nascita: g.luogo_nascita, indirizzo_residenza: g.indirizzo,
          codice_fiscale: g.codice_fiscale, parentela: g.parentela, telefono: g.telefono || ''
        })),
        diaria_mensile: parseFloat(allegato.diaria_mensile) || parseFloat(datiContratto.retta_mensile) || 1500,
        regime: allegato.regime,
        recapiti: garanti.filter(g => g.telefono).map(g => `${g.telefono} ${g.cognome} ${g.nome}`),
        servizio_lavanderia: triState(allegato.servizio_lavanderia),
        cambio_medico_base: triState(allegato.cambio_medico_base),
        servizi_extra: triState(allegato.servizi_extra),
        servizi_extra_note: allegato.servizi_extra_note,
        ritiro_presidi: triState(allegato.ritiro_presidi),
        tipologia_camera: allegato.tipologia_camera,
        altro: allegato.altro,
        data_allegato: allegato.data_allegato
      };
      const res = await api.post('/contratto/allegato', payload, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `Allegato_Accettazione_${ospite.cognome}_${ospite.nome}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Allegato generato e scaricato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore generazione allegato'); }
    setGeneratingAllegato(false);
  };

  const handleFirma = async (id) => {    try { await api.put(`/contratti/${id}/stato`); toast.success('Contratto firmato'); fetchContratti(); }
    catch (e) { toast.error('Errore'); }
  };

  const handleLoadContratto = (c) => {
    const g = c.garanti || (c.garante ? [c.garante] : []);
    setGaranti(g.length > 0 ? g.map(x => ({
      nome: x.nome || '', cognome: x.cognome || '', data_nascita: x.data_nascita || '',
      luogo_nascita: x.luogo_nascita || '', indirizzo: x.indirizzo_residenza || '',
      codice_fiscale: x.codice_fiscale || '', parentela: x.parentela || ''
    })) : [{ ...EMPTY_GARANTE }]);
    setDatiContratto({
      retta_mensile: c.retta_mensile || 1600, data_stipula: c.data_stipula || new Date().toISOString().slice(0, 10),
      luogo_stipula: c.luogo_stipula || 'Celano', note_aggiuntive: c.note_aggiuntive || ''
    });
    toast.info('Dati contratto caricati');
  };

  const handleScanResult = (result) => {
    if (scannerIdx !== null) {
      setGaranti(g => g.map((v, i) => i === scannerIdx ? {
        ...v,
        codice_fiscale: result.codice_fiscale || v.codice_fiscale,
        data_nascita: result.data_nascita || v.data_nascita,
      } : v));
      toast.success(`CF Garante ${scannerIdx + 1} compilato`);
    }
    setScannerIdx(null);
  };

  if (!hasRole('coordinatore', 'amministrazione')) {
    return (<div className="text-center py-12 text-slate-400" data-testid="contratto-no-access"><AlertCircle className="w-10 h-10 mx-auto mb-3" /><p className="text-sm">Accesso riservato a Coordinatore e Amministrazione</p></div>);
  }

  return (
    <div className="space-y-6" data-testid="contratto-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 text-brand" />Genera Contratto di Ospitalita
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Ospite data */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Dati Ospite (automatici)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 rounded-lg bg-slate-50">
                  <div><span className="text-[10px] text-slate-400 block">Nome</span><span className="text-sm font-medium">{ospite.nome}</span></div>
                  <div><span className="text-[10px] text-slate-400 block">Cognome</span><span className="text-sm font-medium">{ospite.cognome}</span></div>
                  <div><span className="text-[10px] text-slate-400 block">Codice Fiscale</span><span className="text-sm font-mono">{ospite.codice_fiscale || '-'}</span></div>
                  <div><span className="text-[10px] text-slate-400 block">Data Nascita</span><span className="text-sm">{ospite.data_nascita ? new Date(ospite.data_nascita).toLocaleDateString('it-IT') : '-'}</span></div>
                  <div><span className="text-[10px] text-slate-400 block">Luogo Nascita</span><span className="text-sm">{ospite.luogo_nascita || '-'}</span></div>
                  <div><span className="text-[10px] text-slate-400 block">Indirizzo</span><span className="text-sm">{ospite.indirizzo_residenza || ospite.provenienza || '-'}</span></div>
                </div>
              </div>

              <Separator />

              {/* Garanti */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />Garanti / Familiari ({garanti.length})
                  </p>
                  <Button variant="outline" size="sm" onClick={addGarante} className="text-xs" data-testid="add-garante-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />Aggiungi Garante
                  </Button>
                </div>
                <div className="space-y-3">
                  {garanti.map((g, i) => (
                    <GaranteForm key={i} garante={g} index={i} total={garanti.length}
                      onChange={updateGarante} onRemove={removeGarante}
                      onScanCF={(idx) => setScannerIdx(idx)} />
                  ))}
                </div>
              </div>

              <Separator />

              {/* Dati contrattuali */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Dati Contrattuali</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Retta Mensile</Label>
                    <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">EUR</span>
                    <Input type="number" min="1600" max="1900" step="50" value={datiContratto.retta_mensile} onChange={e => setDatiContratto(f => ({ ...f, retta_mensile: e.target.value }))} className="pl-11" data-testid="retta-mensile" /></div></div>
                  <div className="space-y-1"><Label className="text-xs">Data Stipula</Label>
                    <DatePickerField value={datiContratto.data_stipula} onChange={v => setDatiContratto(f => ({ ...f, data_stipula: v }))} testId="data-stipula" /></div>
                  <div className="space-y-1"><Label className="text-xs">Luogo Stipula</Label>
                    <Input value={datiContratto.luogo_stipula} onChange={e => setDatiContratto(f => ({ ...f, luogo_stipula: e.target.value }))} data-testid="luogo-stipula" /></div>
                </div>
                <div className="mt-3 space-y-1"><Label className="text-xs">Note Aggiuntive</Label>
                  <Textarea value={datiContratto.note_aggiuntive} onChange={e => setDatiContratto(f => ({ ...f, note_aggiuntive: e.target.value }))} rows={2} placeholder="Annotazioni opzionali..." /></div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving} variant="outline" data-testid="save-contratto-btn">
                  <Save className="w-4 h-4 mr-1" />{saving ? 'Salvataggio...' : 'Salva Bozza'}
                </Button>
                <Button onClick={handleGenerate} disabled={generating} className="bg-brand hover:bg-brand-dark text-white" data-testid="genera-contratto-btn">
                  <Download className="w-4 h-4 mr-1" />{generating ? 'Generazione...' : 'Genera e Scarica DOCX'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Allegato - Dichiarazione di accettazione ricovero */}
          <Card data-testid="allegato-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 text-brand" />Allegato - Dichiarazione di Accettazione Ricovero
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1"><Label className="text-xs">Diaria Mensile</Label>
                  <Input type="number" step="50" placeholder={datiContratto.retta_mensile} value={allegato.diaria_mensile}
                    onChange={e => setAllegato(f => ({ ...f, diaria_mensile: e.target.value }))} data-testid="allegato-diaria" /></div>
                <div className="space-y-1"><Label className="text-xs">Regime</Label>
                  <Select value={allegato.regime} onValueChange={v => setAllegato(f => ({ ...f, regime: v }))}>
                    <SelectTrigger data-testid="allegato-regime"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="privato">Privato</SelectItem>
                      <SelectItem value="convenzionato">Convenzionato</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Tipologia Camera</Label>
                  <Select value={allegato.tipologia_camera} onValueChange={v => setAllegato(f => ({ ...f, tipologia_camera: v }))}>
                    <SelectTrigger data-testid="allegato-camera"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SINGOLA">Singola</SelectItem>
                      <SelectItem value="DOPPIA">Doppia</SelectItem>
                      <SelectItem value="TRIPLA">Tripla</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label className="text-xs">Data</Label>
                  <DatePickerField value={allegato.data_allegato} onChange={v => setAllegato(f => ({ ...f, data_allegato: v }))} testId="allegato-data" /></div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ['servizio_lavanderia', 'Servizio Lavanderia'],
                  ['cambio_medico_base', 'Cambio Medico di Base'],
                  ['servizi_extra', 'Servizi Extra'],
                  ['ritiro_presidi', 'Ritiro Presidi/Ausili'],
                ].map(([key, label]) => (
                  <div className="space-y-1" key={key}><Label className="text-xs">{label}</Label>
                    <Select value={allegato[key]} onValueChange={v => setAllegato(f => ({ ...f, [key]: v }))}>
                      <SelectTrigger data-testid={`allegato-${key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="si">Sì</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="na">Non specificato</SelectItem>
                      </SelectContent>
                    </Select></div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Dettaglio Servizi Extra</Label>
                  <Input value={allegato.servizi_extra_note} placeholder="es. Fondo cassa"
                    onChange={e => setAllegato(f => ({ ...f, servizi_extra_note: e.target.value }))} data-testid="allegato-extra-note" /></div>
                <div className="space-y-1"><Label className="text-xs">Altro</Label>
                  <Input value={allegato.altro} placeholder="Annotazioni"
                    onChange={e => setAllegato(f => ({ ...f, altro: e.target.value }))} data-testid="allegato-altro" /></div>
              </div>

              <Button onClick={handleGenerateAllegato} disabled={generatingAllegato}
                className="bg-brand hover:bg-brand-dark text-white" data-testid="genera-allegato-btn">
                <Download className="w-4 h-4 mr-1" />{generatingAllegato ? 'Generazione...' : 'Genera Allegato DOCX'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Storico */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-slate-500" />Storico Contratti</CardTitle></CardHeader>
            <CardContent>
              {loading ? <p className="text-xs text-slate-400 text-center py-4">Caricamento...</p>
              : contratti.length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Nessun contratto salvato</p>
              : <div className="space-y-2">
                  {contratti.map(c => {
                    const nomiGaranti = (c.garanti || (c.garante ? [c.garante] : [])).map(g => `${g.cognome} ${g.nome}`).join(', ');
                    return (
                      <div key={c.id} className="p-3 rounded-lg border hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => handleLoadContratto(c)} data-testid={`contratto-${c.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-slate-700 truncate">{nomiGaranti || 'Garante'}</span>
                          <Badge variant={c.stato === 'firmato' ? 'default' : 'outline'} className={`text-[10px] flex-shrink-0 ${c.stato === 'firmato' ? 'bg-emerald-100 text-emerald-700' : ''}`}>
                            {c.stato === 'firmato' ? 'Firmato' : 'Bozza'}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleDateString('it-IT')} - EUR {c.retta_mensile?.toFixed(2)}</p>
                        <p className="text-[10px] text-slate-400">Creato da: {c.created_by_name}</p>
                        {c.stato !== 'firmato' && (
                          <Button size="sm" variant="ghost" className="mt-1 h-6 text-[10px] text-emerald-600 px-2"
                            onClick={(e) => { e.stopPropagation(); handleFirma(c.id); }}
                            data-testid={`firma-contratto-${c.id}`}>
                            <CheckCircle className="w-3 h-3 mr-1" />Segna come firmato
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              }
            </CardContent>
          </Card>
        </div>
      </div>

      <BarcodeScannerDialog open={scannerIdx !== null} onClose={() => setScannerIdx(null)} onScanResult={handleScanResult} />
    </div>
  );
}
